import { isProd, site_origin } from '$lib/stores/globals';

/**
 * Transforms an image by constructing a URL for Supabase's Image Transformation service.
 *
 * @param {string} source - a URL or a path to image.
 * @param {ImageTransformOptions} options
 * @returns {string} The URL of the transformed image.
 */
export const img_cdn = (source = '', options = {}) => {

    let path = source.startsWith('/') ? source.slice(1) : source;
    
    // URLSearchParams wants string values; our options contain numbers.
    const params = new URLSearchParams(
        /** @type {Record<string, string>} */ (
            Object.fromEntries(
                Object.entries(options).map(([key, value]) => [
                    key,
                    String(value)
                ])
            )
        )
    ).toString();

    //@ts-ignore
    if (!isProd) return params ? `/${path}?${params}` : `/${path}`;

    //@ts-ignore
    return params
        ? `${site_origin}/img-proxy/${path}?${params}`
        : `${site_origin}/img-proxy/${path}`;
};

/**
 * @param {string} source - a URL or a path to image.
 * @param {ImageTransformOptions} options
 * @returns {string} The URL of the transformed image.
 */
export const img_cdn_srcset = (source = '', options = {}) => {
    // Never do this shit because you will spend hours looking for bugs in production that don't exist in dev
    // if (!isProd) return '';

    if (!options.w || !options.h) return '';

    let opts2x = { ...options};
    let opts3x = { ...options};

    if (options.w) {
        opts2x.w = options.w * 2;
        opts3x.w = options.w * 3;
    }

    if (options.h) {
        opts2x.h = options.h * 2;
        opts3x.h = options.h * 3;
    }

    let srcset = [
        `${img_cdn(source, options)} 1x`,
        `${img_cdn(source, opts2x)} 2x`,
        `${img_cdn(source, opts3x)} 3x`
    ];

    return srcset.join(', ');
}



// Tailwind CSS breakpoint values (in pixels)
const tailwind_breakpoints = {
    xs: 375,
    sm: 640,
    md: 768,
    lg: 1024,
    xl: 1280,
    xxl: 1536
};

/**
 * Generate responsive srcset with multiple breakpoints and density variants.
 * Creates 1x, 2x, and 3x density variants for each specified breakpoint.
 * 
 * @param {string} source - a URL or a path to image
 * @param {Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl', ImageTransformOptions>>} breakpoint_options - Object with breakpoint keys and ImageTransformOptions values
 * @param {(a:string,b:ImageTransformOptions)=>string} transform_function
 * @returns {string} Responsive srcset string with width descriptors
 * 
 * @example
 * img_responsive_srcset('/hero.jpg', {
 *   sm: { w: 640, h: 400, fit: 'cover' },
 *   lg: { w: 1024, h: 600, fit: 'cover' }
 * })
 * // Returns: "/img-proxy/hero.jpg?w=640&h=400&fit=cover 640w, /img-proxy/hero.jpg?w=1280&h=800&fit=cover 1280w, ..."
 */
export const img_responsive_srcset = (source = '', breakpoint_options = {}, transform_function = img_cdn) => {
    const srcset_entries = [];
    
    // Process each breakpoint
    for (const breakpoint in breakpoint_options) {
        const options = breakpoint_options[breakpoint];
        
        if (!options.w) continue; // Skip if no width specified
        
        // Generate 1x, 2x, 3x density variants
        for (const density of [1, 2, 3]) {
            const density_options = { ...options };
            
            if (options.w) {
                density_options.w = options.w * density;
            }
            if (options.h) {
                density_options.h = options.h * density;
            }
            
            const url = transform_function(source, density_options);
            const width_descriptor = `${density_options.w}w`;
            srcset_entries.push(`${url} ${width_descriptor}`);
        }
    }
    
    // Sort by width (ascending) and remove duplicates
    const unique_entries = [...new Set(srcset_entries)];
    unique_entries.sort((a, b) => {
        const width_a = parseInt(a.split(' ')[1]);
        const width_b = parseInt(b.split(' ')[1]);
        return width_a - width_b;
    });
    
    return unique_entries.join(', ');
}

/**
 * Generate responsive sizes attribute from breakpoint configurations.
 * Extracts width values from ImageTransformOptions and creates media query-based sizes.
 * 
 * @param {Partial<Record<'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl', ImageTransformOptions>>} breakpoint_options - Object with breakpoint keys and ImageTransformOptions values
 * @returns {string} CSS sizes attribute value
 * 
 * @example
 * img_responsive_sizes('/hero.jpg', {
 *   xs: { w: 320 },
 *   md: { w: 640 },
 *   lg: { w: 1024 }
 * })
 * // Returns: "(min-width: 1024px) 1024px, (min-width: 768px) 640px, 320px"
 */
export const img_responsive_sizes = (breakpoint_options = {}) => {
    const sizes_entries = [];
    
    // Sort breakpoints by their pixel values (descending for mobile-first)
    const sorted_breakpoints = Object.keys(breakpoint_options).sort((a, b) => {
        return (tailwind_breakpoints[b] || 0) - (tailwind_breakpoints[a] || 0);
    });
    
    for (const [index, breakpoint] of sorted_breakpoints.entries()) {
        const options = breakpoint_options[breakpoint];
        
        if (!options.w) continue; // Skip if no width specified
        
        const width_value = `${options.w}px`;
        
        if (index === sorted_breakpoints.length - 1) {
            // Last (smallest) breakpoint - no media query
            sizes_entries.push(width_value);
        } else {
            // Add media query for this breakpoint
            const min_width = tailwind_breakpoints[breakpoint];
            sizes_entries.push(`(min-width: ${min_width}px) ${width_value}`);
        }
    }
    
    return sizes_entries.join(', ');
}

